import { UserFriendlyNoteFailedReason } from '@heidi/schema/ml-scribe';

import {
  KNOWN_NOTE_GENERATION_ERROR_CODES,
  NoteFailedClientErrorCode,
  NoteFailedSourceErrorCode,
  type NoteFailedErrorCode,
} from '@/models/analytics/contracts/note-failed-error-code';

import { type GenerationAttemptOutcome } from './attempt-outcome';

export type AutoRetryContext = {
  attempt: 1 | 2;
  isOnline: boolean;
  isStillLatest: boolean;
};

export type StopReason =
  | 'completed'
  | 'empty-completed'
  | 'delegated'
  | 'skipped'
  | 'aborted'
  | 'deterministic'
  | 'not-stream-stage'
  | 'personalisation-owned'
  | 'offline'
  | 'stale'
  | 'budget-exhausted';

export type AutoRetryDecision = { kind: 'retry' } | { kind: 'stop'; reason: StopReason };

const isDeterministicFailure = (code: NoteFailedErrorCode): boolean =>
  (KNOWN_NOTE_GENERATION_ERROR_CODES as readonly string[]).includes(code) ||
  code === UserFriendlyNoteFailedReason.transcript_too_short ||
  code === UserFriendlyNoteFailedReason.transcript_not_available ||
  (Object.values(NoteFailedSourceErrorCode) as readonly string[]).includes(code) ||
  code === NoteFailedClientErrorCode.AbortError ||
  /^HTTP_4\d\d$/.test(code);

const stop = (reason: StopReason): AutoRetryDecision => ({ kind: 'stop', reason });

function assertNever(value: never): never {
  throw new Error(`Unhandled generation outcome: ${String(value)}`);
}

export function decideAutoRetry(
  outcome: GenerationAttemptOutcome,
  ctx: AutoRetryContext
): AutoRetryDecision {
  switch (outcome.kind) {
    case 'completed':
      return stop(outcome.isEmpty ? 'empty-completed' : 'completed');
    case 'delegated':
      return stop('delegated');
    case 'skipped':
      return stop('skipped');
    case 'aborted':
      return stop('aborted');
    case 'failed':
      if (outcome.stage !== 'stream') return stop('not-stream-stage');
      if (outcome.personalisationOwned) return stop('personalisation-owned');
      if (
        (outcome.httpStatus !== undefined &&
          outcome.httpStatus >= 400 &&
          outcome.httpStatus < 500) ||
        isDeterministicFailure(outcome.code)
      )
        return stop('deterministic');
      if (ctx.attempt >= 2) return stop('budget-exhausted');
      if (!ctx.isOnline) return stop('offline');
      if (!ctx.isStillLatest) return stop('stale');
      return { kind: 'retry' };
    default:
      return assertNever(outcome);
  }
}
